/* eslint-disable react-hooks/immutability --
   Ported from MobileHighlights.tsx's FLIP/drag implementation; writes element
   styles via refs on a per-frame basis, which the React Compiler lint cannot
   model. All such writes happen in effects and event handlers, never render. */
import * as React from "react";
import { useTranslation } from "react-i18next";
import { animate, motion, useInView, useMotionValue, type MotionStyle } from "motion/react";
import { RiArrowLeftSLine, RiArrowRightSLine } from "@remixicon/react";
import { cn } from "~/lib/utils";
import type { ProductHighlightsItem } from "~/data/products/types";

interface HighlightsMobileCardProps {
  items: ProductHighlightsItem[];
}

const AUTO_ADVANCE_SECONDS = 5;
const PILL_HEIGHT = 48;
const PILL_RADIUS = 24;
const CARD_MAX_WIDTH = 352;
const ARROW_MIN_VISIBLE = 30;
// Must match the row's gap-2 class.
const ROW_GAP = 8;
const DRAG_COMMIT_RATIO = 0.3;
const STEP_MS = 300;
const TEXT_DELAY_MS = 220;
const TEXT_FADE_MS = 250;
const TEXT_RISE_PX = 10;

type PillHandle = {
  root: HTMLElement | null;
  expandedContent: HTMLElement | null;
  chevron: HTMLElement | null;
};

const Pill = React.forwardRef<
  PillHandle,
  {
    item: ProductHighlightsItem;
    t: (key: string) => string;
    isExpanded: boolean;
    arrowSide: "prev" | "next" | null;
    mountText?: boolean;
    textVisible?: boolean;
    chevronVisible?: boolean;
    onClick?: () => void;
    className?: string;
    style?: MotionStyle;
    dragProps?: Record<string, unknown>;
    "data-sid"?: string;
  }
>(function Pill(
  { item, t, isExpanded, arrowSide, mountText, textVisible, chevronVisible, onClick, className, style, dragProps, ...rest },
  ref,
) {
  const rootRef = React.useRef<HTMLButtonElement>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);
  const chevronRef = React.useRef<HTMLSpanElement>(null);
  React.useImperativeHandle(ref, () => ({
    root: rootRef.current,
    expandedContent: contentRef.current,
    chevron: chevronRef.current,
  }));

  return (
    <motion.button
      ref={rootRef}
      {...rest}
      type="button"
      onClick={onClick}
      style={{
        ...(!isExpanded ? { borderRadius: PILL_RADIUS } : null),
        ...(isExpanded
          ? { maxWidth: `min(${CARD_MAX_WIDTH}px, calc(100% - ${2 * (ARROW_MIN_VISIBLE + ROW_GAP)}px))` }
          : null),
        ...style,
      }}
      className={cn(
        "bg-bouncie-100/80 relative shrink-0 overflow-hidden text-left shadow-sm",
        isExpanded ? "rounded-[28px]" : "h-12",
        arrowSide && "w-screen",
        className,
      )}
      {...dragProps}
    >
      {(isExpanded || mountText) && (
        <div
          ref={contentRef}
          className={cn(
            "flex flex-col gap-2 p-6 transition-opacity ease-out",
            textVisible !== false ? "opacity-100" : "opacity-0",
          )}
          style={{ maxWidth: CARD_MAX_WIDTH, transitionDuration: `${STEP_MS}ms` }}
        >
          <span className="text-text-heading text-[18px] leading-[1.1] font-bold">{t(item.title)}</span>
          <span className="text-text-body-secondary text-sm leading-5">{t(item.description)}</span>
        </div>
      )}

      {arrowSide && (
        <span
          ref={chevronRef}
          className={cn(
            "text-bouncie-900 pointer-events-none absolute transition-opacity ease-out",
            chevronVisible !== false ? "opacity-100" : "opacity-0",
            arrowSide === "prev" ? "right-2" : "left-2",
          )}
          style={{ bottom: (PILL_HEIGHT - 20) / 2, transitionDuration: `${STEP_MS}ms` }}
        >
          {arrowSide === "prev" ? <RiArrowLeftSLine className="h-5 w-5" /> : <RiArrowRightSLine className="h-5 w-5" />}
        </span>
      )}
    </motion.button>
  );
});

type EnterFrom = { left: number; width: number; height: number };

function measureExpandedHeight(handle: PillHandle | null, card: PillHandle | null): number | null {
  const text = handle?.expandedContent;
  const cardWidth = card?.root?.getBoundingClientRect().width;
  if (!text || !cardWidth) return null;
  text.style.width = `${cardWidth}px`;
  const height = text.getBoundingClientRect().height;
  text.style.removeProperty("width");
  return height;
}

export function HighlightsMobileCard({ items }: HighlightsMobileCardProps) {
  const { t } = useTranslation("products");
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [userHasInteracted, setUserHasInteracted] = React.useState(false);
  const [stepping, setStepping] = React.useState<{ settleIndex: number } | null>(null);

  const prevIndex = (activeIndex - 1 + items.length) % items.length;
  const nextIndex = (activeIndex + 1) % items.length;
  const prevItem = items[prevIndex];
  const nextItem = items[nextIndex];

  const activeIndexRef = React.useRef(activeIndex);
  React.useEffect(() => {
    activeIndexRef.current = activeIndex;
  }, [activeIndex]);

  const sectionRef = React.useRef<HTMLDivElement>(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.3 });

  const containerRef = React.useRef<HTMLDivElement>(null);
  const rowRef = React.useRef<HTMLDivElement>(null);
  const cardRef = React.useRef<PillHandle>(null);
  const prevRef = React.useRef<PillHandle>(null);
  const nextRef = React.useRef<PillHandle>(null);
  const pendingStepRef = React.useRef<{
    delta: 1 | -1;
    fromCard: { left: number; width: number; height: number };
    enterFrom: EnterFrom;
  } | null>(null);
  const stepTimeoutRef = React.useRef<number | null>(null);
  const textTimeoutRef = React.useRef<number | null>(null);
  const cardX = useMotionValue(0);

  function clearBoxStyle(el: HTMLElement | null) {
    if (!el) return;
    el.style.transition = "";
    el.style.width = "";
    el.style.height = "";
    el.style.transform = "";
    el.style.position = "";
    el.style.left = "";
    el.style.bottom = "";
    el.style.zIndex = "";
    el.style.opacity = "";
  }

  function stepBy(delta: 1 | -1, enterFrom?: EnterFrom) {
    const current = activeIndexRef.current;
    const target = (current + delta + items.length) % items.length;
    const cardEl = cardRef.current?.root;
    const sourceEl = (delta === 1 ? nextRef.current : prevRef.current)?.root;
    const container = containerRef.current;
    if (cardEl && sourceEl && container) {
      const cardRect = cardEl.getBoundingClientRect();
      const containerRect = container.getBoundingClientRect();
      const sourceRect = sourceEl.getBoundingClientRect();
      const from =
        enterFrom ??
        (delta === 1
          ? {
              left: sourceRect.left,
              width: Math.max(PILL_HEIGHT, containerRect.right - sourceRect.left),
              height: sourceRect.height,
            }
          : {
              left: Math.max(sourceRect.left, containerRect.left),
              width: Math.max(PILL_HEIGHT, sourceRect.right - containerRect.left),
              height: sourceRect.height,
            });
      pendingStepRef.current = {
        delta,
        fromCard: { left: cardRect.left, width: cardRect.width, height: cardRect.height },
        enterFrom: { ...from, width: Math.min(from.width, CARD_MAX_WIDTH) },
      };
    }
    setStepping({ settleIndex: current });
    setActiveIndex(target);
  }

  function hardSnap(index: number) {
    pendingStepRef.current = null;
    if (stepTimeoutRef.current !== null) {
      window.clearTimeout(stepTimeoutRef.current);
      stepTimeoutRef.current = null;
    }
    if (textTimeoutRef.current !== null) {
      window.clearTimeout(textTimeoutRef.current);
      textTimeoutRef.current = null;
    }
    [cardRef.current, prevRef.current, nextRef.current].forEach((h) => {
      if (!h) return;
      clearBoxStyle(h.root);
      h.expandedContent?.style.removeProperty("opacity");
      h.expandedContent?.style.removeProperty("width");
      h.expandedContent?.style.removeProperty("transform");
      h.expandedContent?.style.removeProperty("transition");
      h.chevron?.style.removeProperty("opacity");
    });
    cardX.set(0);
    setStepping(null);
    setActiveIndex(index);
  }

  React.useEffect(() => {
    if (!isInView || userHasInteracted) return;
    const interval = setInterval(() => stepBy(1), AUTO_ADVANCE_SECONDS * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- stepBy reads activeIndexRef, not activeIndex.
  }, [isInView, userHasInteracted, items.length]);

  function goToDot(index: number) {
    setUserHasInteracted(true);
    if (index === activeIndex) return;
    const forwardDelta = (index - activeIndex + items.length) % items.length;
    const backwardDelta = (activeIndex - index + items.length) % items.length;
    if (forwardDelta === 1) stepBy(1);
    else if (backwardDelta === 1) stepBy(-1);
    else hardSnap(index);
  }

  React.useLayoutEffect(() => {
    const pending = pendingStepRef.current;
    if (!pending) return;
    pendingStepRef.current = null;

    const container = containerRef.current;
    const row = rowRef.current;
    const card = cardRef.current;
    const settle = pending.delta === 1 ? prevRef.current : nextRef.current;
    const fresh = pending.delta === 1 ? nextRef.current : prevRef.current;
    if (!container || !row || !card?.root || !settle?.root) return;

    if (stepTimeoutRef.current !== null) {
      window.clearTimeout(stepTimeoutRef.current);
      stepTimeoutRef.current = null;
    }

    row.style.height = "";
    [card, settle, fresh].forEach((h) => {
      if (!h) return;
      clearBoxStyle(h.root);
      h.expandedContent?.style.removeProperty("opacity");
      h.expandedContent?.style.removeProperty("width");
      h.expandedContent?.style.removeProperty("transform");
      h.expandedContent?.style.removeProperty("transition");
      h.chevron?.style.removeProperty("opacity");
    });
    cardX.set(0);

    const containerRect = container.getBoundingClientRect();
    const rowRect = row.getBoundingClientRect();
    const toCard = card.root.getBoundingClientRect();
    const toSettle = settle.root.getBoundingClientRect();
    const toFresh = fresh?.root?.getBoundingClientRect() ?? null;

    const fades: Array<[HTMLElement | null | undefined, string]> = [
      [settle.expandedContent, "1"],
      [settle.chevron, "0"],
      [card.expandedContent, "0"],
    ];
    fades.forEach(([el, opacity]) => {
      if (!el) return;
      el.style.transition = "none";
      el.style.opacity = opacity;
    });

    if (card.expandedContent) {
      card.expandedContent.style.width = `${toCard.width}px`;
      card.expandedContent.style.transform = `translateY(${TEXT_RISE_PX}px)`;
    }
    if (settle.expandedContent) settle.expandedContent.style.width = `${pending.fromCard.width}px`;

    row.style.height = `${rowRect.height}px`;
    const pin = (el: HTMLElement, restRect: DOMRect, from: { left: number; width: number; height: number }) => {
      el.style.position = "absolute";
      el.style.bottom = `${rowRect.bottom - restRect.bottom}px`;
      el.style.left = `${from.left - rowRect.left}px`;
      el.style.width = `${from.width}px`;
      el.style.height = `${from.height}px`;
    };
    pin(settle.root, toSettle, pending.fromCard);
    pin(card.root, toCard, pending.enterFrom);
    card.root.style.zIndex = "1";
    if (fresh?.root && toFresh) {
      pin(fresh.root, toFresh, {
        left: pending.delta === 1 ? containerRect.right : containerRect.left - toFresh.width,
        width: toFresh.width,
        height: toFresh.height,
      });
    }

    void container.offsetWidth;

    const boxTransition = `left ${STEP_MS}ms ease-out, width ${STEP_MS}ms ease-out, height ${STEP_MS}ms ease-out`;
    const release = (el: HTMLElement, restRect: DOMRect) => {
      el.style.transition = boxTransition;
      el.style.left = `${restRect.left - rowRect.left}px`;
      el.style.width = `${restRect.width}px`;
      el.style.height = `${restRect.height}px`;
    };
    release(settle.root, toSettle);
    release(card.root, toCard);
    if (fresh?.root && toFresh) release(fresh.root, toFresh);

    [settle.expandedContent, settle.chevron].forEach((el) => {
      if (!el) return;
      el.style.removeProperty("transition");
      el.style.removeProperty("opacity");
    });
    if (card.expandedContent) {
      card.expandedContent.style.transition =
        `opacity ${TEXT_FADE_MS}ms ease-out ${TEXT_DELAY_MS}ms, transform ${TEXT_FADE_MS}ms ease-out ${TEXT_DELAY_MS}ms`;
      card.expandedContent.style.removeProperty("opacity");
      card.expandedContent.style.removeProperty("transform");
    }

    stepTimeoutRef.current = window.setTimeout(() => {
      const activeSide = dragMeta.current.direction;
      const activeEl =
        activeSide === "next" ? nextRef.current?.root : activeSide === "prev" ? prevRef.current?.root : null;
      [card.root, settle.root, fresh?.root ?? null].forEach((el) => {
        if (el && el !== activeEl) clearBoxStyle(el);
      });
      card.expandedContent?.style.removeProperty("width");
      settle.expandedContent?.style.removeProperty("width");
      if (!activeEl) row.style.height = "";
      setStepping(null);
      stepTimeoutRef.current = null;
    }, STEP_MS);

    if (textTimeoutRef.current !== null) window.clearTimeout(textTimeoutRef.current);
    textTimeoutRef.current = window.setTimeout(() => {
      card.expandedContent?.style.removeProperty("transition");
      textTimeoutRef.current = null;
    }, TEXT_DELAY_MS + TEXT_FADE_MS + 50);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeIndex, cardX]);

  const dragMeta = React.useRef({
    direction: null as "prev" | "next" | null,
    offsetBase: 0,
    lockOffset: 0,
    commitDistance: 0,
    startHeight: 0,
    targetHeight: 0,
  });

  const [dragActiveSide, setDragActiveSide] = React.useState<"prev" | "next" | null>(null);
  React.useLayoutEffect(() => {
    if (dragActiveSide === null) return;
    const height = measureExpandedHeight(
      dragActiveSide === "next" ? nextRef.current : prevRef.current,
      cardRef.current,
    );
    if (height !== null) dragMeta.current.targetHeight = Math.max(height, PILL_HEIGHT);
  }, [dragActiveSide]);

  function releaseNubStyle(pillEl: HTMLElement | null) {
    if (!pillEl) return;
    pillEl.style.transition = `width ${STEP_MS}ms ease-out, height ${STEP_MS}ms ease-out, transform ${STEP_MS}ms ease-out`;
    pillEl.style.width = "";
    pillEl.style.height = "";
    pillEl.style.transform = "";
    window.setTimeout(() => {
      clearBoxStyle(pillEl);
    }, STEP_MS);
  }

  function resetDragMeta() {
    dragMeta.current = {
      direction: null,
      offsetBase: 0,
      lockOffset: 0,
      commitDistance: 0,
      startHeight: 0,
      targetHeight: 0,
    };
    setDragActiveSide(null);
  }

  function handleCardDrag(_event: unknown, info: { offset: { x: number } }) {
    setUserHasInteracted(true);
    const meta = dragMeta.current;
    const offset = info.offset.x - meta.offsetBase;

    if (meta.offsetBase !== 0) cardX.set(offset);

    if (stepTimeoutRef.current !== null) return;

    if (!meta.direction) {
      if (Math.abs(offset) < 4) return;
      const direction: "prev" | "next" = offset < 0 ? "next" : "prev";
      const nubHandle = direction === "next" ? nextRef.current : prevRef.current;
      const container = containerRef.current;
      const nubEl = nubHandle?.root;
      if (!nubEl || !container) return;

      nubEl.style.transition = "";

      const containerRect = container.getBoundingClientRect();
      const nubRect = nubEl.getBoundingClientRect();
      const cardRect = cardRef.current?.root?.getBoundingClientRect();

      meta.direction = direction;
      meta.lockOffset = offset;
      meta.commitDistance = containerRect.width * DRAG_COMMIT_RATIO;
      meta.startHeight = nubRect.height;
      meta.targetHeight = cardRect?.height ?? PILL_HEIGHT;
      setDragActiveSide(direction);
    }

    const nubEl = (meta.direction === "next" ? nextRef.current : prevRef.current)?.root;
    const otherNubEl = (meta.direction === "next" ? prevRef.current : nextRef.current)?.root;
    if (!nubEl || !meta.direction) return;
    const nubOffset = offset - meta.lockOffset;
    const towardCommit = meta.direction === "next" ? -nubOffset : nubOffset;
    const progress = meta.commitDistance > 0 ? Math.min(Math.max(towardCommit / meta.commitDistance, 0), 1) : 1;
    const height = meta.startHeight + (meta.targetHeight - meta.startHeight) * progress;
    nubEl.style.height = `${height}px`;
    nubEl.style.transform = `translateX(${nubOffset}px)`;
    if (otherNubEl) otherNubEl.style.transform = `translateX(${nubOffset}px)`;

    if (towardCommit >= meta.commitDistance) {
      const container = containerRef.current;
      const dir = meta.direction;
      let enterFrom: EnterFrom | undefined;
      if (container) {
        const containerRect = container.getBoundingClientRect();
        const nubRect = nubEl.getBoundingClientRect();
        enterFrom =
          dir === "next"
            ? {
                left: nubRect.left,
                width: Math.max(PILL_HEIGHT, containerRect.right - nubRect.left),
                height: nubRect.height,
              }
            : {
                left: Math.max(nubRect.left, containerRect.left),
                width: Math.max(PILL_HEIGHT, nubRect.right - containerRect.left),
                height: nubRect.height,
              };
      }
      meta.offsetBase = info.offset.x;
      meta.direction = null;
      meta.lockOffset = 0;
      setDragActiveSide(null);
      stepBy(dir === "next" ? 1 : -1, enterFrom);
    }
  }

  function handleCardDragEnd() {
    if (dragMeta.current.direction !== null) {
      releaseNubStyle(prevRef.current?.root ?? null);
      releaseNubStyle(nextRef.current?.root ?? null);
    }
    animate(cardX, 0, { type: "tween", duration: 0.3, ease: "easeOut" });
    resetDragMeta();
  }

  function arrowSlotProps(side: "prev" | "next", index: number) {
    return {
      isExpanded: false,
      arrowSide: side,
      mountText: (stepping !== null && stepping.settleIndex === index) || dragActiveSide === side,
      textVisible: false,
      chevronVisible: dragActiveSide !== side,
    } as const;
  }

  return (
    <motion.div
      ref={sectionRef}
      initial={{ opacity: 0, y: 24 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 24 }}
      transition={{ duration: 0.55, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <div className="relative mb-4 flex aspect-[343/274] w-full items-center justify-center overflow-hidden rounded-2xl border border-dashed border-neutral-200 bg-white/60">
        <div
          className="flex aspect-[85/44] w-[85%] items-center justify-center rounded-full"
          style={{ backgroundColor: "var(--neutral-80)" }}
        >
          <span className="font-display text-2xl font-extrabold tracking-wide text-[#e0219b]">FPO</span>
        </div>
      </div>

      <div ref={containerRef} className="wrap-bleed relative self-stretch overflow-hidden">
        <div ref={rowRef} className="relative flex items-end justify-center gap-2 py-4">
          <Pill
            ref={prevRef}
            data-sid="highlights-mobile-pill:prev"
            item={prevItem}
            t={t}
            onClick={() => {
              setUserHasInteracted(true);
              stepBy(-1);
            }}
            {...arrowSlotProps("prev", prevIndex)}
          />

          <Pill
            ref={cardRef}
            data-sid="highlights-mobile-pill:card"
            item={items[activeIndex]}
            t={t}
            isExpanded
            arrowSide={null}
            className="touch-pan-y active:cursor-grabbing"
            style={{ x: cardX }}
            dragProps={{
              drag: "x",
              onDrag: handleCardDrag,
              onDragEnd: handleCardDragEnd,
            }}
          />

          <Pill
            ref={nextRef}
            data-sid="highlights-mobile-pill:next"
            item={nextItem}
            t={t}
            onClick={() => {
              setUserHasInteracted(true);
              stepBy(1);
            }}
            {...arrowSlotProps("next", nextIndex)}
          />
        </div>
      </div>

      <div className="mt-4 flex justify-center gap-2">
        {items.map((item, index) => {
          const isActive = index === activeIndex;
          return (
            <button
              key={index}
              data-sid={`highlights-mobile-dot:${index}`}
              type="button"
              aria-label={t(item.title)}
              aria-current={isActive}
              onClick={() => goToDot(index)}
              className={cn(
                "relative h-2 overflow-hidden rounded-full bg-neutral-100 transition-[width] duration-300",
                isActive ? "w-12" : "w-2",
              )}
            >
              <span
                className="bg-bouncie-900 absolute inset-y-0 left-0 rounded-full"
                style={{
                  width: isActive ? "100%" : "0%",
                  transition: !isActive
                    ? "none"
                    : userHasInteracted
                      ? "width 0.3s ease"
                      : `width linear ${AUTO_ADVANCE_SECONDS}s`,
                }}
              />
            </button>
          );
        })}
      </div>
    </motion.div>
  );
}
